import { CircleAlert, TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { Project } from "@/api/queries";
import { formatDuration } from "@/lib/format";
import { cn } from "@/lib/utils";

import { budgetUsage } from "./lib";

const barColor = { ok: "bg-primary-solid", warning: "bg-warning-solid", over: "bg-danger-solid" } as const;

/** Time spent against the budget; warnings carry an icon and text, not only color. */
export function BudgetBar({ project }: { project: Pick<Project, "tracked_seconds" | "budget_minutes"> }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? "ru";
  const spent = formatDuration(project.tracked_seconds, lang);
  const usage = budgetUsage(project);

  if (!usage || !project.budget_minutes) {
    return (
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 text-sm">
        <span className="text-muted">
          {t("projects.tracked")}: <span className="font-medium text-fg tabular-nums">{spent}</span>
        </span>
        <span className="text-xs text-muted">{t("projects.noBudget")}</span>
      </div>
    );
  }

  const budget = formatDuration(project.budget_minutes * 60, lang);
  const label = t("projects.budgetPercent", { percent: usage.percent });
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 text-sm">
        <span className="font-medium tabular-nums">{t("projects.trackedOf", { spent, budget })}</span>
        <span className="text-xs text-muted tabular-nums">{label}</span>
      </div>
      <div
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.min(usage.percent, 100)}
        className="h-1.5 overflow-hidden rounded-full bg-surface-muted"
      >
        <div
          className={cn("h-full rounded-full", barColor[usage.level])}
          style={{ width: `${Math.min(usage.percent, 100)}%` }}
        />
      </div>
      {usage.level !== "ok" && (
        <p
          className={cn(
            "flex items-center gap-1 text-xs font-medium",
            usage.level === "over" ? "text-danger" : "text-warning",
          )}
        >
          {usage.level === "over" ? (
            <CircleAlert className="size-3.5" aria-hidden="true" />
          ) : (
            <TriangleAlert className="size-3.5" aria-hidden="true" />
          )}
          {usage.level === "over" ? t("projects.budgetOver") : t("projects.budgetWarning")}
        </p>
      )}
    </div>
  );
}
